import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  TextField,
  LinearProgress,
  InputAdornment,
  SvgIcon,
  IconButton,
  Button,
  ButtonGroup,
  Typography,
  CircularProgress,
  Hidden,
  Divider
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import GenericPicker from "../utilities/GenericPicker";
import {
  BCPAngleLeft,
  BCPAngleRight,
  BCPAnglesLeft,
  BCPAnglesRight,
  BCPCircleArrowUp,
  BCPSearch,
  BCPXmark
} from "../icons/bcp/BCPIcons";
import { isAppMode } from "../../global/AppOverlay";
import {
  getLocalStorage,
  setLocalStorage
} from "../layout/cookies/CookieFunctions";

export default function ListResponsive(props) {
  const {
    originalList,
    searchProps,
    rowProps,
    doPages,
    initialRowsPerPage,
    doSearch,
    loadMore,
    loadMoreHref,
    hasMore,
    scrollToTop,
    actionButton,
    rowsPerPageArray,
    pagingFunction,
    endOfPaging,
    sx,
    spacing,
    authContext,
    userRowsPerPage,
    rowReverse,
    customComponent,
    actionButtonBottom,
    centerNav,
    buttonGroup,
    children
  } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "components.ListResponsive"
  });
  const theme = useTheme();

  const styles = {
    searchField: {
      "& .MuiOutlinedInput-input": {
        padding: theme?.spacing(1)
      },
      "& .MuiOutlinedInput-adornedEnd": {
        paddingRight: theme?.spacing(0)
      }
    },
    buttonBottom: {
      color: theme?.palette?.text?.primary,
      textTransform: "none",
      fontWeight: "600",
      fontSize: "18px",
      "&:hover": {
        backgroundColor: theme?.palette?.secondary?.main,
        color: theme?.palette?.secondary?.contrastText
      }
    }
  };

  const [processing, setProcessing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  const [currentSearch, setCurrentSearch] = useState(null);
  const [currentList, setCurrentList] = useState(null);

  const searchInputRef = useRef(null);

  const [page, setPage] = useState(0);

  let iRowsPerPage = initialRowsPerPage || 10;
  if (userRowsPerPage && getLocalStorage(`${userRowsPerPage}Rows`)) {
    try {
      const getGetCachedData = getLocalStorage(`${userRowsPerPage}Rows`);
      if (getGetCachedData) {
        iRowsPerPage = parseInt(getGetCachedData);
      } else {
        throw new Error("No cached data found");
      }
    } catch (err) {
      if (!iRowsPerPage) {
        iRowsPerPage = initialRowsPerPage || 10;
      }
    }
  }

  const [rowsPerPage, setRowsPerPage] = useState(iRowsPerPage);
  var rowsPerPageOptions =
    rowsPerPageArray !== undefined
      ? rowsPerPageArray
      : [12, 24, 48, 96, { label: t("all"), value: -1 }];
  if (
    initialRowsPerPage &&
    rowsPerPageOptions &&
    !rowsPerPageOptions.includes(initialRowsPerPage)
  ) {
    for (var i = 0; i < rowsPerPageOptions.length; i++) {
      if (Number.isInteger(rowsPerPageOptions[i])) {
        if (rowsPerPageOptions[i] > initialRowsPerPage) {
          rowsPerPageOptions.splice(i, 0, initialRowsPerPage);
          break;
        }
      } else if (rowsPerPageOptions[i].label) {
        rowsPerPageOptions.splice(i, 0, initialRowsPerPage);
        break;
      }
    }
  }

  const widthSx = spacing ? { width: `calc(100% + ${8 * spacing}px)` } : {};

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleFirstPageButtonClick = (event) => {
    handleChangePage(event, 0);
  };

  const handleBackButtonClick = (event) => {
    handleChangePage(event, page - 1);
  };

  const handleNextButtonClick = async (event) => {
    if (
      pagingFunction &&
      page >=
        Math.ceil((currentList ? currentList.length : 0) / rowsPerPage) - 1
    ) {
      setProcessing(true);
      await pagingFunction();
      setProcessing(false);
    }
    handleChangePage(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    handleChangePage(
      event,
      Math.max(
        0,
        Math.ceil((currentList ? currentList.length : 0) / rowsPerPage) - 1
      )
    );
  };

  const performSearch = (searchText) => {
    if (!searchText || searchText === "" || !originalList?.length) {
      setCurrentList(originalList);
    } else {
      var filteredList =
        searchProps?.performSearch(originalList, searchText) || [];
      setCurrentList(filteredList);
    }
  };

  const handleSearchChange = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setCurrentSearch(searchValue);
    performSearch(searchValue);
  };

  const handleClearSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    setCurrentSearch(null);
    performSearch("");
  };

  useEffect(() => {
    const check = async () => {
      setCurrentList(originalList || null);
      if (currentSearch) performSearch(currentSearch);
    };
    check();
    // eslint-disable-next-line
  }, [originalList]);

  const handleScroll = () => {
    if (window.pageYOffset > 200) {
      if (!showScrollToTop) setShowScrollToTop(true);
    } else {
      if (showScrollToTop) setShowScrollToTop(false);
    }
  };
  useEffect(() => {
    if (!scrollToTop) return;
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  });

  return (
    <>
      {Boolean(doSearch || actionButton) && (
        <Grid
          container
          spacing={2}
          flexDirection={rowReverse ? "row-reverse" : "row"}
          sx={{ mb: 2 }}
          justifyContent="space-between"
        >
          {doSearch ? (
            <>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  sx={styles.searchField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon fontSize="small" color="action">
                          <BCPSearch
                            style={{
                              "font-size": "16px",
                              width: "16px",
                              color:
                                processing || !originalList?.length
                                  ? theme.palette.text.disabled
                                  : theme.palette.text.primary
                            }}
                          />
                        </SvgIcon>
                      </InputAdornment>
                    ),
                    endAdornment: currentSearch && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClearSearch}
                          size="small"
                          sx={{
                            padding: theme?.spacing(0.5),
                            "&:hover": {
                              backgroundColor: theme?.palette?.action?.hover
                            }
                          }}
                        >
                          <BCPXmark
                            style={{
                              "font-size": "14px",
                              width: "14px",
                              color: theme.palette.text.secondary
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  placeholder={searchProps.label}
                  variant="outlined"
                  inputRef={searchInputRef}
                  onChange={handleSearchChange}
                  disabled={processing || !originalList?.length}
                />
              </Grid>

              {actionButtonBottom && (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap"
                  }}
                >
                  {Array.isArray(actionButtonBottom) ? (
                    <>
                      {actionButtonBottom.map((button, index) => {
                        if (!button) return <></>;

                        return (
                          <Button
                            sx={styles.buttonBottom}
                            key={index}
                            {...button}
                          >
                            {button.label}
                          </Button>
                        );
                      })}
                    </>
                  ) : !actionButtonBottom?.label ||
                    !actionButtonBottom?.props ? (
                    <Grid item xs={12}>
                      {actionButtonBottom}
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Button {...actionButtonBottom.props}>
                        {actionButtonBottom.label}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          ) : null}

          {doSearch && actionButton && (
            <Hidden mdUp>
              <Grid item xs={12}>
                <Divider />
              </Grid>
            </Hidden>
          )}

          {actionButton &&
            (Array.isArray(actionButton) ? (
              <Grid item xs={12}>
                {buttonGroup === false ? (
                  <>
                    {actionButton.map((button, index) => (
                      <Button sx={styles.buttonBottom} key={index} {...button}>
                        {button.label}
                      </Button>
                    ))}
                  </>
                ) : (
                  <ButtonGroup fullWidth>
                    {actionButton.map((action, index) => {
                      return (
                        <Button key={index} {...action.props}>
                          {action.label}
                        </Button>
                      );
                    })}
                  </ButtonGroup>
                )}
              </Grid>
            ) : !actionButton?.label || !actionButton?.props ? (
              <Grid item xs={12}>
                {actionButton}
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Button {...actionButton.props}>{actionButton.label}</Button>
              </Grid>
            ))}
        </Grid>
      )}
      {customComponent && customComponent}
      {children}
      <Grid
        container
        spacing={spacing}
        sx={{ width: "100%", ...widthSx, ...sx }}
      >
        {rowProps.headings}
        {!originalList || !currentList || processing ? (
          <Grid item xs={12} sx={{ height: "50px" }}>
            <LinearProgress />
          </Grid>
        ) : (
          (rowsPerPage > 0 && doPages
            ? currentList.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
            : currentList
          ).map(rowProps.mapFunction)
        )}
      </Grid>
      {(hasMore || hasMore === undefined) &&
        (loadMore || loadMoreHref) &&
        originalList &&
        currentList && (
          <Grid container sx={{ mt: 1 }}>
            <Grid item xs={12} sx={{ height: "30px" }}>
              {loadingMore && <LinearProgress />}
            </Grid>
            <Grid item xs={12} textAlign="center">
              <Button
                variant="outlined"
                color="primary"
                sx={{ minWidth: "200px" }}
                href={loadMoreHref}
                onClick={
                  loadMore
                    ? async (e) => {
                        setLoadingMore(true);
                        await loadMore();
                        setLoadingMore(false);
                      }
                    : null
                }
                disabled={loadingMore}
              >
                {t("loadMore")}
              </Button>
            </Grid>
          </Grid>
        )}
      {doPages && originalList && currentList && (
        <Grid container sx={{ mt: 1, alignItems: "center" }}>
          {rowsPerPageOptions?.length > 1 && !centerNav && (
            <Grid item xs={12} sm={6}>
              <GenericPicker
                label={t("itemsPerPage")}
                required
                inclAny={false}
                list={rowsPerPageOptions}
                value={rowsPerPage}
                formControlSX={{
                  "& .MuiSelect-select": {
                    p: "4px"
                  }
                }}
                handleChange={(e) => {
                  setRowsPerPage(e.target.value);
                  if (
                    userRowsPerPage &&
                    authContext?.authState?.cookieConsent
                  ) {
                    try {
                      setLocalStorage(`${userRowsPerPage}Rows`, e.target.value);
                      const getCachedItems = getLocalStorage("CachedItems");
                      const cachedItems = getCachedItems
                        ? JSON.parse(getCachedItems)
                        : {};
                      cachedItems[`${userRowsPerPage}Rows`] = true;
                      setLocalStorage(
                        "CachedItems",
                        JSON.stringify(cachedItems)
                      );
                    } catch (err) {}
                  }
                }}
              />
            </Grid>
          )}

          <Grid
            item
            xs={12}
            sm={centerNav ? 12 : rowsPerPageOptions?.length > 1 ? 6 : 12}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: centerNav ? "center" : ["center", "flex-end"]
              }}
            >
              {!centerNav && (
                <>
                  <Typography sx={{ display: "table-cell" }}>
                    {rowsPerPage > 0
                      ? `${page * rowsPerPage + 1}-${
                          page * rowsPerPage + rowsPerPage > currentList?.length
                            ? currentList?.length
                            : page * rowsPerPage + rowsPerPage
                        } ${t("of", { defaultValue: "of" })} ${
                          !processing
                            ? `${currentList?.length}${
                                endOfPaging === false ? "+" : ""
                              }`
                            : ""
                        }`
                      : t("items", {
                          count: currentList?.length,
                          defaultValue: "{{count}} items"
                        })}
                    {processing && <CircularProgress size={12} />}
                  </Typography>
                </>
              )}
              <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0 || processing}
                aria-label="first page"
                size="large"
              >
                {theme.direction === "rtl" ? (
                  <BCPAnglesRight
                    style={{ "font-size": "16px", width: "16px" }}
                  />
                ) : (
                  <BCPAnglesLeft
                    style={{ "font-size": "16px", width: "16px" }}
                  />
                )}
              </IconButton>
              <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0 || processing}
                aria-label="previous page"
                size="large"
              >
                {theme.direction === "rtl" ? (
                  <BCPAngleRight
                    style={{ "font-size": "16px", width: "16px" }}
                  />
                ) : (
                  <BCPAngleLeft
                    style={{ "font-size": "16px", width: "16px" }}
                  />
                )}
              </IconButton>

              {centerNav && (
                <Typography sx={{ display: "table-cell" }}>
                  {rowsPerPage > 0
                    ? `${page * rowsPerPage + 1}-${
                        page * rowsPerPage + rowsPerPage > currentList?.length
                          ? currentList?.length
                          : page * rowsPerPage + rowsPerPage
                      } ${t("of", { defaultValue: "of" })} ${
                        !processing
                          ? `${currentList?.length}${
                              endOfPaging === false ? "+" : ""
                            }`
                          : ""
                      }`
                    : t("items", {
                        count: currentList?.length,
                        defaultValue: "{{count}} items"
                      })}
                  {processing && <CircularProgress size={12} />}
                </Typography>
              )}
              <IconButton
                onClick={handleNextButtonClick}
                disabled={
                  processing || endOfPaging === undefined
                    ? page >=
                      Math.ceil(
                        (currentList ? currentList.length : 0) / rowsPerPage
                      ) -
                        1
                    : endOfPaging === false
                      ? false
                      : page >=
                        Math.ceil(
                          (currentList ? currentList.length : 0) / rowsPerPage
                        ) -
                          1
                }
                aria-label="next page"
                size="large"
              >
                {theme.direction === "rtl" ? (
                  <BCPAngleLeft
                    style={{ "font-size": "16px", width: "16px" }}
                  />
                ) : (
                  <BCPAngleRight
                    style={{ "font-size": "16px", width: "16px" }}
                  />
                )}
              </IconButton>
              <IconButton
                onClick={handleLastPageButtonClick}
                disabled={
                  processing ||
                  endOfPaging === false ||
                  page >=
                    Math.ceil(
                      (currentList ? currentList.length : 0) / rowsPerPage
                    ) -
                      1
                }
                aria-label="last page"
                size="large"
              >
                {theme.direction === "rtl" ? (
                  <BCPAnglesLeft
                    style={{ "font-size": "16px", width: "16px" }}
                  />
                ) : (
                  <BCPAnglesRight
                    style={{ "font-size": "16px", width: "16px" }}
                  />
                )}
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      )}
      {scrollToTop && showScrollToTop && !isAppMode() && (
        <Button
          variant="contained"
          color="info"
          size="large"
          onClick={() => {
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
          }}
          sx={{
            position: "fixed",
            bottom: "40px",
            right: "10px",
            minWidth: "unset",
            borderRadius: "50%",
            padding: "unset",
            zIndex: 2
          }}
        >
          <BCPCircleArrowUp
            style={{ "font-size": "2.5rem", width: "2.5rem" }}
          />
        </Button>
      )}
    </>
  );
}
